import { useMemo } from 'react';
import { useProductService, useRegionCurrency, type Product } from '@skin-seoul/service';
import { processProductsData } from '../../utils/collectionProductDataHelpers';
import { useProductReviewData } from './useProductReviewData';
import { useProductGridEffects } from './useProductGridEffects';
import type { FilterSummary } from '../../types/filters';

/**
 * Common pagination parameters structure
 */
export interface PaginationParams {
  page: number;
  limit: number;
}

/**
 * Common return type for product grid hooks
 */
export interface ProductGridReturn {
  products: Product[];
  reviewStatsMap: Record<string, unknown>;
  total: number;
  currentPage: number;
  hasProducts: boolean;
  summary?: FilterSummary;
}

/**
 * Props for shared product grid logic
 */
interface UseProductGridCommonProps {
  currentPage: number;
  productsPerPage: number;
}

/**
 * Shared utilities and common logic for product grid hooks
 */
export const useProductGridCommon = ({ currentPage, productsPerPage }: UseProductGridCommonProps) => {
  const { currency } = useRegionCurrency();
  const productService = useProductService();

  // Build pagination parameters
  const pagination = useMemo(
    () => ({
      page: currentPage,
      limit: productsPerPage,
    }),
    [currentPage, productsPerPage],
  );

  return {
    currency,
    productService,
    pagination,
  };
};

/**
 * Shared hook for processing product data and handling side effects
 * Used after API data is fetched
 */
export const useProductDataProcessing = ({
  data,
  currentPage,
  onProductCountChange,
  onFilterSummaryChange,
}: {
  data: { products?: Product[]; total?: number; summary?: FilterSummary };
  currentPage: number;
  onProductCountChange?: (count: number) => void;
  onFilterSummaryChange?: (summary: FilterSummary | undefined) => void;
}) => {
  // Process products data
  const products = useMemo(() => {
    const result = processProductsData(data);
    return result;
  }, [data]);

  // Get review statistics
  const { reviewStatsMap } = useProductReviewData(products);

  // Handle side effects
  useProductGridEffects({
    data,
    onProductCountChange,
    onFilterSummaryChange,
  });

  // Return standardized data structure
  return {
    products,
    reviewStatsMap,
    total: data?.total || 0,
    currentPage,
    hasProducts: products.length > 0,
    summary: data?.summary,
  };
};
